import React, { useState, useEffect } from 'react';
import {
  Box, Card, CardContent, Typography, TextField, Button, Alert, 
  Divider, MenuItem, CircularProgress, InputAdornment, IconButton,
} from '@mui/material';
import { Visibility, VisibilityOff, Lock, AdminPanelSettings } from '@mui/icons-material';
import { useAppSelector, useAppDispatch } from '../../hooks/useAppHooks';
import { setCredentials } from '../../features/auth/authSlice';
import apiClient, { setTokens } from '../../utils/apiClient';

const ChangePassword: React.FC = () => {
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.auth);
  const isSuperAdmin = user?.role === 'super_admin';

  // Own password change
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState<{ type: 'success' | 'error' | 'info'; message: string } | null>(null);

  // Admin reset
  const [users, setUsers] = useState<any[]>([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [adminNewPassword, setAdminNewPassword] = useState('');
  const [showAdminNew, setShowAdminNew] = useState(false);
  const [adminLoading, setAdminLoading] = useState(false);
  const [adminAlert, setAdminAlert] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  // Load user list for super admin
  useEffect(() => {
    if (isSuperAdmin) {
      apiClient.get('/admin/users').then(res => {
        setUsers(res.data?.users || res.data || []);
      }).catch(() => {});
    }
  }, [isSuperAdmin]);

  const handleChangePassword = async () => {
    setAlert(null);
    if (!currentPassword || !newPassword || !confirmPassword) {
      setAlert({ type: 'error', message: 'Please fill in all fields' });
      return;
    }
    if (newPassword.length < 8) {
      setAlert({ type: 'error', message: 'New password must be at least 8 characters' });
      return;
    }
    if (newPassword !== confirmPassword) {
      setAlert({ type: 'error', message: 'New passwords do not match' });
      return;
    }
    if (currentPassword === newPassword) {
      setAlert({ type: 'error', message: 'New password must differ from current password' });
      return;
    }

    setLoading(true);
    try {
      const res = await apiClient.post('/auth/change-password', {
        current_password: currentPassword,
        new_password: newPassword,
      });
      
      // Update tokens in store and apiClient so session stays valid
      if (res.data.access_token) {
        setTokens(res.data.access_token, res.data.refresh_token);
        dispatch(setCredentials({
          accessToken: res.data.access_token,
          refreshToken: res.data.refresh_token,
          user: user!,
        }));
      }
      
      setAlert({ type: 'success', message: 'Password changed successfully. All other sessions have been logged out.' });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (e: any) {
      setAlert({ type: 'error', message: e.response?.data?.detail || 'Failed to change password' });
    } finally {
      setLoading(false);
    }
  };

  const handleAdminReset = async () => {
    setAdminAlert(null);
    if (!selectedUserId || !adminNewPassword) {
      setAdminAlert({ type: 'error', message: 'Please select a user and enter a new password' });
      return;
    }
    if (adminNewPassword.length < 8) {
      setAdminAlert({ type: 'error', message: 'Password must be at least 8 characters' });
      return;
    }

    setAdminLoading(true);
    try {
      const res = await apiClient.post('/auth/admin-reset-password', {
        user_id: selectedUserId,
        new_password: adminNewPassword,
      });
      setAdminAlert({ type: 'success', message: res.data.message || 'Password reset successfully' });
      setAdminNewPassword('');
      setSelectedUserId('');
    } catch (e: any) {
      setAdminAlert({ type: 'error', message: e.response?.data?.detail || 'Failed to reset password' });
    } finally {
      setAdminLoading(false);
    }
  };

  // Password strength indicator
  const getStrength = (pw: string) => {
    if (!pw) return { label: '', color: '' };
    let score = 0;
    if (pw.length >= 8) score++;
    if (pw.length >= 12) score++;
    if (/[A-Z]/.test(pw) && /[a-z]/.test(pw)) score++;
    if (/[0-9]/.test(pw)) score++;
    if (/[^A-Za-z0-9]/.test(pw)) score++;
    if (score <= 2) return { label: 'Weak', color: 'error.main' };
    if (score <= 3) return { label: 'Fair', color: 'warning.main' };
    return { label: 'Strong', color: 'success.main' };
  };

  const strength = getStrength(newPassword);

  return (
    <Box sx={{ p: 3, maxWidth: 700, mx: 'auto' }}>
      <Typography variant="h4" fontWeight={700} gutterBottom>
        <Lock sx={{ mr: 1, verticalAlign: 'middle' }} />
        Change Password
      </Typography>

      {/* ===== Self Password Change (all roles) ===== */}
      <Card sx={{ mb: 4 }}>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="h6" fontWeight={600} gutterBottom>
            Change Your Password
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Enter your current password and choose a new one. All other active sessions will be logged out.
          </Typography>

          {alert && <Alert severity={alert.type} sx={{ mb: 2 }} onClose={() => setAlert(null)}>{alert.message}</Alert>}

          <TextField
            label="Current Password"
            type={showCurrent ? 'text' : 'password'}
            fullWidth
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            sx={{ mb: 2 }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowCurrent(!showCurrent)} edge="end" size="small">
                    {showCurrent ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <TextField
            label="New Password"
            type={showNew ? 'text' : 'password'}
            fullWidth
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            sx={{ mb: 1 }}
            helperText={newPassword ? `Strength: ${strength.label}` : 'Minimum 8 characters'}
            FormHelperTextProps={{ sx: { color: strength.color } }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowNew(!showNew)} edge="end" size="small">
                    {showNew ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <TextField
            label="Confirm New Password"
            type="password"
            fullWidth
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            sx={{ mb: 3 }}
            error={!!confirmPassword && confirmPassword !== newPassword}
            helperText={confirmPassword && confirmPassword !== newPassword ? 'Passwords do not match' : ''}
          />

          <Button
            variant="contained"
            onClick={handleChangePassword}
            disabled={loading || !currentPassword || !newPassword || !confirmPassword}
            startIcon={loading ? <CircularProgress size={16} /> : <Lock />}
            fullWidth
          >
            {loading ? 'Changing...' : 'Change Password'}
          </Button>
        </CardContent>
      </Card>

      {/* ===== Super Admin: Reset Any User's Password ===== */}
      {isSuperAdmin && (
        <Card>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              <AdminPanelSettings sx={{ mr: 1, verticalAlign: 'middle', fontSize: 22 }} />
              Reset User Password
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              As super admin, you can reset any user's password. The user's active sessions will be terminated.
            </Typography>

            {adminAlert && <Alert severity={adminAlert.type} sx={{ mb: 2 }} onClose={() => setAdminAlert(null)}>{adminAlert.message}</Alert>}

            <TextField
              select
              label="Select User"
              fullWidth
              value={selectedUserId}
              onChange={(e) => setSelectedUserId(e.target.value)}
              sx={{ mb: 2 }}
            >
              <MenuItem value=""><em>Select a user</em></MenuItem>
              {users.map((u) => (
                <MenuItem key={u.id} value={u.id}>
                  {u.email} ({u.role})
                </MenuItem>
              ))}
            </TextField>

            <TextField
              label="New Password for User"
              type={showAdminNew ? 'text' : 'password'}
              fullWidth
              value={adminNewPassword}
              onChange={(e) => setAdminNewPassword(e.target.value)}
              sx={{ mb: 3 }}
              helperText="Minimum 8 characters"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowAdminNew(!showAdminNew)} edge="end" size="small">
                      {showAdminNew ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Button
              variant="contained"
              color="warning"
              onClick={handleAdminReset}
              disabled={adminLoading || !selectedUserId || !adminNewPassword}
              startIcon={adminLoading ? <CircularProgress size={16} /> : <AdminPanelSettings />}
              fullWidth
            >
              {adminLoading ? 'Resetting...' : 'Reset User Password'}
            </Button>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default ChangePassword;
